import { AlertOctagon, Inbox } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Accessible loading label announced once for a skeleton group. */
  label?: string;
}

export function Skeleton({ className, label, ...props }: SkeletonProps) {
  return (
    <div
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('animate-pulse rounded-control bg-border-subtle', className)}
      {...props}
    />
  );
}

export interface EmptyStateProps {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  compact?: boolean;
}

export function EmptyState({ title, description, icon, action, className, compact }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-12', className)}>
      <div className="flex size-12 items-center justify-center rounded-pill bg-surface-subtle text-text-muted [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden />}
      </div>
      <div className="grid max-w-md gap-1">
        <p className="text-card-title font-semibold text-text-primary">{title}</p>
        {description ? <div className="text-body text-text-secondary">{description}</div> : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: React.ReactNode;
  /** Technical reference (error code / request id). Never include message content. */
  reference?: string;
  action?: React.ReactNode;
  className?: string;
}

export function ErrorState({ title = 'No pudimos cargar esta información', description, reference, action, className }: ErrorStateProps) {
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12 text-center', className)}>
      <div className="flex size-12 items-center justify-center rounded-pill bg-danger-bg text-danger [&_svg]:size-5">
        <AlertOctagon aria-hidden />
      </div>
      <div className="grid max-w-md gap-1">
        <p className="text-card-title font-semibold text-text-primary">{title}</p>
        {description ? <div className="text-body text-text-secondary">{description}</div> : null}
        {reference ? <p className="cm-tabular text-helper text-text-muted">Referencia: {reference}</p> : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/** Visually hidden but available to assistive technology. */
export function VisuallyHidden({ children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className="sr-only" {...props}>
      {children}
    </span>
  );
}
